import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import ViewSelector from "../components/ViewSelector";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Users,
  Loader2,
  TrendingUp,
  TrendingDown,
  Calendar,
  Briefcase,
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  Activity,
  RefreshCw,
  LayoutGrid,
  List,
  Table
} from "lucide-react";

// Simple bar chart component
const SimpleBarChart = ({ data, title, color = "#0C314C" }) => {
  if (!data || data.length === 0) return null;
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{title}</p>
      <div className="space-y-1">
        {data.slice(0, 6).map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <span className="text-xs w-24 truncate text-slate-600 dark:text-slate-400">{item.label}</span>
            <div className="flex-1 bg-slate-100 dark:bg-slate-700 rounded-full h-4 overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%`,
                  backgroundColor: color 
                }}
              />
            </div>
            <span className="text-xs font-medium w-8 text-right">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Simple donut chart component
const SimpleDonutChart = ({ data, title }) => {
  if (!data || data.length === 0) return null;
  const total = data.reduce((acc, d) => acc + d.value, 0);
  let currentAngle = 0;
  
  const colors = ["#0C314C", "#45E69A", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#10B981"];
  
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 100 100" className="w-24 h-24">
        {data.map((item, idx) => {
          const percentage = total > 0 ? (item.value / total) * 100 : 0;
          const angle = (percentage / 100) * 360;
          const startAngle = currentAngle;
          currentAngle += angle;
          
          const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
          const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
          const x2 = 50 + 40 * Math.cos((startAngle + angle - 90) * Math.PI / 180);
          const y2 = 50 + 40 * Math.sin((startAngle + angle - 90) * Math.PI / 180);
          
          const largeArc = angle > 180 ? 1 : 0;
          
          return (
            <path
              key={idx}
              d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={colors[idx % colors.length]}
              className="transition-all duration-300 hover:opacity-80"
            />
          );
        })}
        <circle cx="50" cy="50" r="25" fill="white" className="dark:fill-slate-800" />
        <text x="50" y="50" textAnchor="middle" dy=".3em" className="text-sm font-bold fill-slate-800 dark:fill-white">
          {total}
        </text>
      </svg>
      <div className="space-y-1">
        {data.slice(0, 5).map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[idx % colors.length] }} />
            <span className="text-xs text-slate-600 dark:text-slate-400">{item.label}</span>
            <span className="text-xs font-medium">({item.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function DashboardRRHHPage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [periodo, setPeriodo] = useState("mes");
  const [currentView, setCurrentView] = useState("cards");
  const { getAuthHeaders } = useAuth();

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/dashboard/rrhh?periodo=${periodo}`, getAuthHeaders());
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, periodo]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getStatusBadge = (value, type = "default") => {
    if (type === "trend") {
      const isPositive = value > 0;
      return (
        <Badge className={`border-0 ${isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
          {Math.abs(value)}%
        </Badge>
      );
    }
    return <Badge variant="secondary">{value}</Badge>;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="dashboard-rrhh-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Dashboard Ejecutivo RRHH
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Métricas y tendencias de Gestión Humana
            </p>
          </div>
          <div className="flex items-center gap-3">
            <ViewSelector currentView={currentView} onViewChange={setCurrentView} />
            <Select value={periodo} onValueChange={setPeriodo}>
              <SelectTrigger className="w-40" data-testid="periodo-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="semana">Esta Semana</SelectItem>
                <SelectItem value="mes">Este Mes</SelectItem>
                <SelectItem value="trimestre">Este Trimestre</SelectItem>
                <SelectItem value="año">Este Año</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchStats} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-10 h-10 animate-spin text-[#0C314C]" />
          </div>
        ) : stats ? (
          <div className="space-y-6">
            {/* KPI Row 1 - Main Metrics */}
            <div className="kpi-carousel grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Total Empleados</p>
                      <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.empleados?.total || 0}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-green-600">{stats.empleados?.activos || 0} activos</span>
                        <span className="text-xs text-slate-400">•</span>
                        <span className="text-xs text-red-500">{stats.empleados?.inactivos || 0} inactivos</span>
                      </div>
                    </div>
                    <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Users className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Vacaciones Pendientes</p>
                      <p className="text-3xl font-bold text-amber-600">{stats.vacaciones?.pendientes || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">{stats.vacaciones?.aprobadas_periodo || 0} aprobadas este {periodo}</p>
                    </div>
                    <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <Calendar className="w-7 h-7 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Vacantes Abiertas</p>
                      <p className="text-3xl font-bold text-purple-600">{stats.reclutamiento?.vacantes_abiertas || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">{stats.reclutamiento?.candidatos_activos || 0} candidatos en proceso</p>
                    </div>
                    <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <Briefcase className="w-7 h-7 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Contrataciones</p>
                      <p className="text-3xl font-bold text-green-600">{stats.reclutamiento?.contratados_periodo || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">Este {periodo}</p>
                    </div>
                    <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <UserPlus className="w-7 h-7 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Solicitudes por Estado */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PieChart className="w-5 h-5 text-[#0C314C]" />
                    Solicitudes de RRHH por Estado
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleDonutChart 
                    data={stats.solicitudes_por_estado || []}
                    title="Distribución"
                  />
                </CardContent>
              </Card>

              {/* Pipeline de Reclutamiento */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Activity className="w-5 h-5 text-[#0C314C]" />
                    Pipeline de Reclutamiento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart 
                    data={stats.pipeline_reclutamiento || []}
                    title="Candidatos por Etapa"
                    color="#45E69A"
                  />
                </CardContent>
              </Card>

              {/* Vacaciones por Departamento */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BarChart3 className="w-5 h-5 text-[#0C314C]" />
                    Días de Vacaciones por Departamento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart 
                    data={stats.vacaciones_por_departamento || []}
                    title="Días consumidos"
                    color="#3B82F6"
                  />
                </CardContent>
              </Card>

              {/* Solicitudes Recientes */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Clock className="w-5 h-5 text-[#0C314C]" />
                    Actividad Reciente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(stats.actividad_reciente || []).slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            item.tipo === 'vacacion_aprobada' ? 'bg-green-100 text-green-600' :
                            item.tipo === 'contratacion' ? 'bg-purple-100 text-purple-600' :
                            item.tipo === 'solicitud' ? 'bg-blue-100 text-blue-600' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {item.tipo === 'vacacion_aprobada' ? <CheckCircle className="w-4 h-4" /> :
                             item.tipo === 'contratacion' ? <UserPlus className="w-4 h-4" /> :
                             item.tipo === 'solicitud' ? <Clock className="w-4 h-4" /> :
                             <Activity className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white">{item.titulo}</p>
                            <p className="text-xs text-slate-500">{item.descripcion}</p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">{item.fecha}</span>
                      </div>
                    ))}
                    {(!stats.actividad_reciente || stats.actividad_reciente.length === 0) && (
                      <p className="text-center text-slate-500 py-4">Sin actividad reciente</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Summary Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="dark:bg-slate-800 dark:border-slate-700 border-l-4 border-l-green-500">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="text-2xl font-bold text-green-600">{stats.resumen?.solicitudes_resueltas || 0}</p>
                      <p className="text-sm text-slate-500">Solicitudes Resueltas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dark:bg-slate-800 dark:border-slate-700 border-l-4 border-l-amber-500">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{stats.resumen?.solicitudes_pendientes || 0}</p>
                      <p className="text-sm text-slate-500">Pendientes de Revisión</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dark:bg-slate-800 dark:border-slate-700 border-l-4 border-l-red-500">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-8 h-8 text-red-500" />
                    <div>
                      <p className="text-2xl font-bold text-red-600">{stats.resumen?.solicitudes_rechazadas || 0}</p>
                      <p className="text-sm text-slate-500">Rechazadas este {periodo}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">No hay datos disponibles</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
